import React from 'react';
import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Typography,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useResetPasswordMutation,
} from '../store/services/users';
import { useGetShopsQuery } from '../store/services/shops';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface User {
  _id: string;
  username: string;
  email: string;
  mobile_number: string;
  role: string;
  shop_id?: string;
  is_active: boolean;
  is_blocked: boolean;
  created_at: string;
  updated_at: string;
}

const validationSchema = yup.object().shape({
  username: yup.string().required('Username is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().when('isEditing', {
    is: false,
    then: (schema) => schema.required('Password is required').min(8, 'Password must be at least 8 characters'),
    otherwise: (schema) => schema.notRequired()
  }),
  mobile_number: yup.string().required('Mobile number is required'),
  role: yup.string().required('Role is required'),
  shop_id: yup.string().when('role', {
    is: (val: string) => val === 'ShopEmployee' || val === 'ShopOwner',
    then: (schema) => schema.required('Shop is required'),
    otherwise: (schema) => schema.notRequired().nullable()
  }),
  isEditing: yup.boolean()
});

export default function Users() {
  const [open, setOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { data: users = [], isLoading, error, refetch } = useGetUsersQuery();
  const { data: shops } = useGetShopsQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [resetPassword] = useResetPasswordMutation();
  const currentUser = useSelector((state: RootState) => state.auth.user) as User | null;

  // Refetch user data when component mounts to ensure fresh data after login
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Early return if no current user
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Access Denied</Typography>
        <Typography>Please log in to access this page.</Typography>
      </Box>
    );
  }

  // Check if the current user is a ShopEmployee, who doesn't have permission to manage users
  if (currentUser.role === 'ShopEmployee') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">User Management</Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          As a Shop Employee, you don't have permission to manage users. 
          Please contact your Shop Owner if you need assistance with user management.
        </Alert>
      </Box>
    );
  }

  // Filter users based on current user's role
  const filteredUsers = useMemo(() => {
    if (!users || users.length === 0) return [];

    // SuperAdmin can see all users
    if (currentUser.role === 'SuperAdmin') {
      return users;
    }
    
    // ShopOwner can see only shop employees in their shop
    if (currentUser.role === 'ShopOwner') {
      return users.filter(user => 
        user.role === 'ShopEmployee' && 
        user.shop_id === currentUser.shop_id
      );
    }

    // Other roles shouldn't see any users
    return [];
  }, [users, currentUser]);

  // Determine available roles based on current user's role
  const availableRoles = useMemo(() => {
    switch (currentUser?.role) {
      case 'SuperAdmin':
        return ['SuperAdmin', 'ShopOwner', 'ShopEmployee'];
      case 'ShopOwner':
        return ['ShopEmployee'];
      default:
        return [];
    }
  }, [currentUser?.role]);

  // Check if the current user can perform actions on a specific user
  const canManageUser = (user: User) => {
    if (!currentUser) return false;
    
    // SuperAdmin can manage all users except themselves
    if (currentUser.role === 'SuperAdmin') {
      return currentUser._id !== user._id;
    }
    
    // ShopOwner can only manage their shop's employees
    if (currentUser.role === 'ShopOwner') {
      return user.role === 'ShopEmployee' && user.shop_id === currentUser.shop_id;
    }
    
    return false;
  };

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      mobile_number: '',
      role: '',
      shop_id: currentUser?.role === 'ShopOwner' ? currentUser.shop_id : '',
      isEditing: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedUser) {
          await updateUser({
            id: selectedUser._id,
            data: {
              username: values.username,
              email: values.email,
              mobile_number: values.mobile_number,
              role: values.role,
              shop_id: values.shop_id || undefined,
            },
          }).unwrap();
        } else {
          await createUser({
            username: values.username,
            email: values.email,
            password: values.password,
            mobile_number: values.mobile_number,
            role: values.role,
            shop_id: values.shop_id || undefined,
          }).unwrap();
        }
        handleClose();
      } catch (error) {
        console.error('Failed to save user:', error);
      }
    },
  });

  const handleClickOpen = () => {
    setSelectedUser(null);
    formik.resetForm({
      values: {
        username: '',
        email: '',
        password: '',
        mobile_number: '',
        role: currentUser?.role === 'ShopOwner' ? 'ShopEmployee' : '',
        shop_id: currentUser?.role === 'ShopOwner' ? currentUser.shop_id : '',
        isEditing: false,
      }
    });
    setOpen(true);
  };

  const handleEdit = (user: User) => {
    if (!canManageUser(user)) return;
    
    setSelectedUser(user);
    formik.resetForm({
      values: {
        username: user.username,
        email: user.email,
        password: '',
        mobile_number: user.mobile_number,
        role: user.role,
        shop_id: user.shop_id || '',
        isEditing: true,
      }
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleDelete = async (id: string, user: User) => {
    if (!canManageUser(user)) return;
    
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(id).unwrap();
      } catch (error) {
        // console.error('Failed to delete user:', error);
      }
    }
  };

  const handleToggleBlock = async (user: User) => {
    if (!canManageUser(user)) return;
    
    try {
      await updateUser({
        id: user._id,
        data: { is_blocked: !user.is_blocked },
      }).unwrap();
    } catch (error) {
      // console.error('Failed to toggle user block status:', error);
    }
  };

  const handleResetPasswordClick = (user: User) => {
    setSelectedUser(user);
    setResetPasswordOpen(true);
  };

  const resetPasswordFormik = useFormik({
    initialValues: {
      new_password: '',
      confirm_password: '',
    },
    validationSchema: yup.object({
      new_password: yup.string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters'),
      confirm_password: yup.string()
        .required('Please confirm password')
        .oneOf([yup.ref('new_password')], 'Passwords must match'),
    }),
    onSubmit: async (values) => {
      if (!selectedUser) return;

      try {
        await resetPassword({
          id: selectedUser._id,
          new_password: values.new_password,
        }).unwrap();
        setResetPasswordOpen(false);
        resetPasswordFormik.resetForm();
        alert('Password reset successful');
      } catch (error) {
        // console.error('Failed to reset password:', error);
        alert('Failed to reset password');
      }
    },
  });

  const canResetPassword = (user: User) => {
    if (!currentUser) return false;
    
    if (currentUser.role === 'SuperAdmin') {
      return user.role !== 'SuperAdmin' || user._id !== currentUser._id;
    }
    
    if (currentUser.role === 'ShopOwner') {
      return user.role === 'ShopEmployee' && user.shop_id === currentUser.shop_id;
    }
    
    return false;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    // console.error('Error fetching users:', error);
    return <div>Error loading users</div>;
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">
          {currentUser.role === 'ShopOwner' ? 'Manage Shop Employees' : 'Manage Users'}
        </Typography>
        <Button variant="contained" onClick={handleClickOpen}>
          {currentUser.role === 'ShopOwner' ? 'Add Employee' : 'Add User'}
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Shop</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user: User) => {
              return (
                <TableRow key={user._id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.mobile_number}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role}
                      color={
                        user.role === 'SuperAdmin'
                          ? 'error'
                          : user.role === 'ShopOwner'
                          ? 'warning'
                          : 'info'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {shops?.find((shop: any) => shop._id === user.shop_id)?.name || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_blocked ? 'Blocked' : 'Active'}
                      color={user.is_blocked ? 'error' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    {currentUser.role === 'SuperAdmin' ? (
                      // SuperAdmin can manage all users except themselves
                      currentUser._id !== user._id && (
                        <>
                          <IconButton onClick={() => handleEdit(user)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(user._id, user)}>
                            <DeleteIcon />
                          </IconButton>
                          <IconButton onClick={() => handleToggleBlock(user)}>
                            <BlockIcon />
                          </IconButton>
                          {canResetPassword(user) && (
                            <IconButton onClick={() => handleResetPasswordClick(user)}>
                              <LockResetIcon />
                            </IconButton>
                          )}
                        </>
                      )
                    ) : currentUser.role === 'ShopOwner' ? (
                      // ShopOwner can only manage their shop's employees
                      user.role === 'ShopEmployee' && 
                      user.shop_id === currentUser.shop_id && (
                        <>
                          <IconButton onClick={() => handleEdit(user)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(user._id, user)}>
                            <DeleteIcon />
                          </IconButton>
                          <IconButton onClick={() => handleToggleBlock(user)}>
                            <BlockIcon />
                          </IconButton>
                          {canResetPassword(user) && (
                            <IconButton onClick={() => handleResetPasswordClick(user)}>
                              <LockResetIcon />
                            </IconButton>
                          )}
                        </>
                      )
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              id="username"
              name="username"
              label="Username"
              value={formik.values.username}
              onChange={formik.handleChange}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
            />
            <TextField
              fullWidth
              margin="dense"
              id="email"
              name="email"
              label="Email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            {!selectedUser && (
              <TextField
                fullWidth
                margin="dense"
                id="password"
                name="password"
                label="Password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            )}
            <TextField
              fullWidth
              margin="dense"
              id="mobile_number"
              name="mobile_number"
              label="Mobile Number"
              value={formik.values.mobile_number}
              onChange={formik.handleChange}
              error={formik.touched.mobile_number && Boolean(formik.errors.mobile_number)}
              helperText={formik.touched.mobile_number && formik.errors.mobile_number}
            />
            <FormControl fullWidth margin="dense">
              <FormLabel>Role</FormLabel>
              <RadioGroup
                name="role"
                value={formik.values.role}
                onChange={formik.handleChange}
              >
                {availableRoles.map((role) => (
                  <FormControlLabel
                    key={role}
                    value={role}
                    control={<Radio />}
                    label={role}
                  />
                ))}
              </RadioGroup>
              {formik.touched.role && formik.errors.role && (
                <FormHelperText error>{formik.errors.role}</FormHelperText>
              )}
            </FormControl>
            {(formik.values.role === 'ShopEmployee' || formik.values.role === 'ShopOwner') && (
              <TextField
                select
                fullWidth
                margin="dense"
                id="shop_id"
                name="shop_id"
                label="Shop"
                value={currentUser?.role === 'ShopOwner' ? currentUser.shop_id : formik.values.shop_id}
                onChange={formik.handleChange}
                error={formik.touched.shop_id && Boolean(formik.errors.shop_id)}
                helperText={formik.touched.shop_id && formik.errors.shop_id}
                disabled={currentUser?.role === 'ShopOwner'}
              >
                {shops?.map((shop: any) => (
                  <MenuItem key={shop._id} value={shop._id}>
                    {shop.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {selectedUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onClose={() => {
        setResetPasswordOpen(false);
        resetPasswordFormik.resetForm();
      }}>
        <DialogTitle>Reset Password for {selectedUser?.username}</DialogTitle>
        <form onSubmit={resetPasswordFormik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="dense"
              id="new_password"
              name="new_password"
              label="New Password"
              type="password"
              value={resetPasswordFormik.values.new_password}
              onChange={resetPasswordFormik.handleChange}
              error={resetPasswordFormik.touched.new_password && Boolean(resetPasswordFormik.errors.new_password)}
              helperText={resetPasswordFormik.touched.new_password && resetPasswordFormik.errors.new_password}
            />
            <TextField
              fullWidth
              margin="dense"
              id="confirm_password"
              name="confirm_password"
              label="Confirm Password"
              type="password"
              value={resetPasswordFormik.values.confirm_password}
              onChange={resetPasswordFormik.handleChange}
              error={resetPasswordFormik.touched.confirm_password && Boolean(resetPasswordFormik.errors.confirm_password)}
              helperText={resetPasswordFormik.touched.confirm_password && resetPasswordFormik.errors.confirm_password}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setResetPasswordOpen(false);
              resetPasswordFormik.resetForm();
            }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Reset Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}